import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { FileUpload } from '@mui/icons-material';
import BaseModal from '../../../utils/BaseModal';

interface PaymentProviderUploadModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (file: File, mfaCode?: string) => void | Promise<void>;
    methodId: string | null;
    methodName: string | null;
    loading?: boolean;
}

const PaymentMethodUploadModal: React.FC<PaymentProviderUploadModalProps> = ({
                                                                                   open,
                                                                                   onClose,
                                                                                   onConfirm,
                                                                                   methodId,
                                                                                   methodName,
                                                                                   loading = false,
                                                                               }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    useEffect(() => {
        if (!open) {
            setSelectedFile(null);
        }
    }, [open]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleSubmit = (mfaCode?: string) => {
        if (selectedFile) {
            onConfirm(selectedFile, mfaCode);
        }
    };

    return (
        <BaseModal
            open={open}
            title={`Upload Configuration for ${methodName || 'Method'}`}
            onAbort={onClose}
            onSubmit={handleSubmit}
            abortText="Cancel"
            submitText="Upload & Save"
            submitColor="primary"
            loading={loading}
            submitDisabled={!selectedFile || !methodId}
            mfaMode="REQUIRED"
            maxWidth="sm"
        >
            <div className="flex flex-col gap-4 py-2">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                    Select an Excel file (.xlsx or .xls) to update the country configurations for <strong className="text-gray-900 dark:text-white">{methodName}</strong>.
                </p>

                <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-neutral-700 rounded-lg cursor-pointer bg-gray-50 dark:bg-neutral-900/50 hover:bg-gray-100 dark:hover:bg-neutral-800/50 transition-colors">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <FileUpload className="w-8 h-8 mb-2 text-gray-400" />
                            <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
                                <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-400">
                                XLSX or XLS files
                            </p>
                        </div>
                        <input type="file" accept=".xlsx, .xls" className="hidden" onChange={handleFileChange} />
                    </label>
                </div>

                {selectedFile && (
                    <div className="flex items-center justify-between p-2.5 bg-neutral-100 dark:bg-neutral-800/60 rounded text-xs font-mono text-gray-700 dark:text-gray-300 border border-neutral-200 dark:border-neutral-700">
                        <span className="truncate">{selectedFile.name}</span>
                        <Button size="small" color="error" onClick={() => setSelectedFile(null)}>
                            Remove
                        </Button>
                    </div>
                )}
            </div>
        </BaseModal>
    );
};

export default PaymentMethodUploadModal;